import React, { useState, useRef } from "react";
import { Box, Button, Grid, LinearProgress, Typography } from "@mui/material";
import FileCard from "./FileCard";
import ViewSizeSelector from "./ViewSizeSelector";
import useViewSize from "../hooks/useViewSize";
import FilePreviewDataset from "../../../admin/model_dataset/component/FilePreviewDataset";
import ConfirmDialog from "../../../../shared/component/ConfirmDialog";
import { useDeleteFileUser } from "../hooks/useDeleteFileUser";
import { uploadFileUser } from "../services/uploadFileUser";
import FilePreviewUser from "./FilePreviewUser";

/**
 * FolderView component displays files under a specific subfolder.
 * Props:
 * - subfolder: the selected subfolder object containing files
 * - onFolderUpdate: callback to refresh folder data after upload/delete
 * - userId: ID of the current user
 * - folderId: ID of the parent folder
 */
export default function FolderView({
  folder,
  subfolder,
  files,
  filesLoading,
  filesError,
  onFolderUpdate,
  userId,
  folderId,
}) {
  const [viewSize, setViewSize] = useState("medium");
  const { getGridSizes } = useViewSize(viewSize);
  const gridSizes = getGridSizes();

  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const { deleteFile } = useDeleteFileUser();

  if (!subfolder) {
    return <Typography>Select a subfolder to view its files.</Typography>;
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      await uploadFileUser(
        file,
        folderId,
        userId,
        subfolder.id,
        subfolder.subfolderName,
        (progress) => {
          setUploadProgress(progress);
        }
      );
      setUploadProgress(0);
      setUploading(false);
      if (onFolderUpdate) {
        onFolderUpdate();
      }
    } catch (error) {
      setUploadError(error.message || "Upload failed");
      setUploading(false);
    }
  };

  const handlePreviewOpen = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };
  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleDeleteRequest = (file) => {
    console.log("handleDeleteRequest invoked from FolderView.jsx");
    console.log("File object:", file);
    setFileToDelete(file);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    console.log(
      "handlerConfirmDelete invoked after handleDeleteRequest from FolderView.jsx"
    );
    if (!fileToDelete) return;
    setConfirmOpen(false);
    try {
      //Safe yung file dito na check ko, na seset naman properly
      await deleteFile(fileToDelete, subfolder.id);
      if (onFolderUpdate) {
        onFolderUpdate();
      }
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setFileToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setFileToDelete(null);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={handleUploadClick}
          disabled={
            uploading ||
            (folder &&
              (folder.status === "approved" || folder.status === "pending"))
          }
        >
          Upload File
        </Button>
        <ViewSizeSelector viewSize={viewSize} setViewSize={setViewSize} />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>
      {uploading && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}
      {uploadError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {uploadError}
        </Typography>
      )}
      {files.length === 0 ? (
        <Typography>No files found in this subfolder.</Typography>
      ) : (
        <Grid container spacing={2}>
          {files.map((file) => (
            <Grid item key={file.storagePath} {...gridSizes}>
              <FileCard
                file={file}
                onPreview={() => handlePreviewOpen(file)}
                onDelete={() => handleDeleteRequest(file)}
                viewSize={viewSize}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <FilePreviewUser
        open={previewOpen}
        onClose={handlePreviewClose}
        file={previewFile}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this file? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
}
